import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Storefront, ListChecks, Sparkle, ClockCounterClockwise, CalendarBlank } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { StatusBadge } from "../StatusBadge";
import { ProgressBar } from "./ProgressBar";
import { MARKETPLACES, PLATFORM_BY_KEY } from "../../platforms";
import { fmtRelative, fmtDateTime, progressFromPromo } from "../../campaignUtils";

export function CampaignCard({ promo, index = 0 }) {
  const { t } = useTranslation();
  const c = promo.counters || {};
  // Fix 02/11/2026: real progress from the published posts (server summary), not "AI prepared".
  const real = progressFromPromo(promo);
  const total = real ? real.total : (Number(promo.max_items) || c.selected || 0);
  const processed = real ? real.processed : (c.selected || 0);
  const remaining = real ? real.remaining : Math.max(0, total - processed);
  const pct = real ? real.pct : (total ? Math.round((processed / total) * 100) : 0);
  const lastActivity = real?.lastActivity || promo.updated_at;
  const nextAt = real?.nextPublishAt || promo.next_run_at;
  const mkt = MARKETPLACES.find((m) => m.key === promo.source_platform);

  const Metric = ({ label, value, tone }) => (
    <div>
      <div className="promo-overline">{label}</div>
      <div className={cn("mt-0.5 text-lg font-medium tabular-nums", tone)}>{value}</div>
    </div>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05, ease: "easeOut" }}
    >
      <Link
        to={`/promotions/campaigns/${promo.id}`}
        className="group block rounded-lg border border-border bg-card p-5 transition-colors hover:bg-accent/30"
        data-testid={`campaign-card-${promo.id}`}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-medium tracking-tight">{promo.name}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                {mkt ? <mkt.Icon size={12} /> : <Storefront size={12} />}
                {mkt?.label || promo.source_platform || "—"}
              </span>
              <span className="flex items-center gap-1"><ListChecks size={12} />{t(`promo.wizard.strategy.opt.${promo.selection_strategy}.title`)}</span>
              <span className="flex items-center gap-1"><Sparkle size={12} />{t(`promo.wizard.mode.opt.${promo.generation_mode}.title`)}</span>
            </div>
          </div>
          <StatusBadge status={promo.status} label={t(`promo.campaigns.status.${promo.status}`)} />
        </div>

        {/* Platforms */}
        <div className="mt-4 flex items-center gap-1.5">
          {(promo.platforms || []).map((p) => {
            const meta = PLATFORM_BY_KEY[p];
            return meta ? (
              <span key={p} className="flex h-6 w-6 items-center justify-center rounded-md border border-border bg-accent/40" title={meta.label}>
                <meta.Icon size={12} />
              </span>
            ) : null;
          })}
        </div>

        {/* Progress */}
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">{t("promo.campaigns.progress")}</span>
            <span className="tabular-nums font-medium">{pct}%</span>
          </div>
          <ProgressBar value={pct} tone={promo.status === "completed" ? "success" : "brand"} />
        </div>

        {/* Metrics */}
        <div className="mt-4 grid grid-cols-3 gap-3 border-t border-border/60 pt-4">
          <Metric label={t("promo.campaigns.total")} value={total} />
          <Metric label={t("promo.campaigns.processed")} value={processed} tone="text-brand" />
          <Metric label={t("promo.campaigns.remaining")} value={remaining} />
        </div>

        {/* Footer */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border/60 pt-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><ClockCounterClockwise size={12} />{t("promo.campaigns.lastActivity")}: {fmtRelative(lastActivity)}</span>
          {real?.errors > 0 && <span className="text-destructive">{t("promo.campaigns.errors", { count: real.errors })}</span>}
          <span className="flex items-center gap-1"><CalendarBlank size={12} />{t("promo.campaigns.next")}: {nextAt ? fmtDateTime(nextAt) : "—"}</span>
        </div>
      </Link>
    </motion.div>
  );
}
